import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  /** @type {number} */
  #index = 0;

  /** @type {{ x: number, y: number } | null} */
  #swipe = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.#onTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('touchstart', this.#onTouchStart);
    this.refs.dialog.removeEventListener('touchend', this.#onTouchEnd);
  }

  #onTouchStart = (event) => {
    if (event.touches.length !== 1) {
      this.#swipe = null;
      return;
    }
    const touch = event.touches[0];
    this.#swipe = { x: touch.clientX, y: touch.clientY };
  };

  #onTouchEnd = (event) => {
    if (!this.#swipe || event.touches.length) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - this.#swipe.x;
    const dy = touch.clientY - this.#swipe.y;
    this.#swipe = null;

    const active = this.refs.media?.[this.#index];
    if (active?.querySelector('drag-zoom-wrapper')?.isZoomed?.()) return;
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    if (dx < 0) this.showNext();
    else this.showPrevious();
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();
    this.selectThumbnail(Number(index), { behavior: 'instant' });
    this.refs.dialog.showModal();
  }

  /**
   * @param {Event} [event]
   */
  showNext(event) {
    event?.preventDefault();
    const count = this.refs.media?.length ?? 0;
    if (count < 2) return;
    this.selectThumbnail((this.#index + 1) % count, { behavior: 'instant' });
  }

  /**
   * @param {Event} [event]
   */
  showPrevious(event) {
    event?.preventDefault();
    const count = this.refs.media?.length ?? 0;
    if (count < 2) return;
    this.selectThumbnail((this.#index - 1 + count) % count, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    image.src = highResolutionUrl;
    image.removeAttribute('srcset');
    this.#highResImagesLoaded.add(highResolutionUrl);
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNext(event);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPrevious(event);
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails, counter } = this.refs;
    if (!media?.length) return;
    if (isNaN(index) || index < 0 || index >= media.length) return;

    this.#index = index;

    media.forEach((el, i) => {
      el.classList.toggle('is-active', i === index);
      el.querySelector('drag-zoom-wrapper')?.reset?.();
    });

    const targetThumbnail = thumbnails?.children?.[index];
    if (targetThumbnail instanceof HTMLElement) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });

      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const targetImage = media[index];
    if (targetImage) this.loadHighResolutionImage(targetImage);

    if (counter instanceof HTMLElement) {
      counter.hidden = media.length < 2;
      counter.textContent = `${index + 1} / ${media.length}`;
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
